import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, Plus, Server } from 'lucide-react';
import { Site, Job, JobSavePayload } from '../../types/site';
import { Rack } from '../../types';
import { useProjectStore } from '../../store/projectStore';
import { useSitesStore, generateSiteId } from '../../store/sitesStore';
import JobModal from './JobModal';
import JobDrawer from './JobDrawer';

interface SiteDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  site: Site | null;
  onSwitchToDesigner?: () => void;
  onOpenDesignModal?: () => void;
}

const SiteDetailsDrawer: React.FC<SiteDetailsDrawerProps> = ({ 
  isOpen, 
  onClose, 
  site,
  onSwitchToDesigner,
  onOpenDesignModal
}) => {
  // Always render the drawer in the DOM for smooth transitions
  // Only hide it visually when closed or no site
  const shouldShow = isOpen && site;

  const {
    getDesignsForCurrentProject,
    openDesignTab,
    getCurrentDesign,
    appTheme,
    setSelectedRack,
    setRackBuilderOpen,
  } = useProjectStore();
  const { sites, saveSite, saveSiteJobs, createRecurrenceRule, updateRecurrenceRule, endRecurrenceRule, recurrenceRules, createJob } = useSitesStore();
  const isDarkTheme = appTheme === 'dark';
  const [showAddDesignSubmenu, setShowAddDesignSubmenu] = useState(false);
  const [showDesignList, setShowDesignList] = useState(false);
  const submenuRef = useRef<HTMLDivElement>(null);
  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [editingRecurrenceSchedule, setEditingRecurrenceSchedule] = useState(false);
  const [drawerView, setDrawerView] = useState<'site' | 'job'>('site');

  // Get the latest site from the project store to ensure we have the most up-to-date data
  // This will automatically update when currentProject changes
  const currentSite = React.useMemo(() => {
    if (!site) return site;
    return sites.find((s) => s.id === site.id) || site;
  }, [site, sites]);

  // Collapsible section states - persist across drawer open/close
  const [expandedSections, setExpandedSections] = useState(() => {
    // Try to load from localStorage, otherwise use defaults
    const saved = localStorage.getItem(`siteDrawerSections_${site?.id || 'default'}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        // If parsing fails, use defaults
      }
    }
    return {
      siteDetails: true, // Default to expanded
      network: false,
      jobs: false,
      equipment: false,
      maintenance: false,
      storage: false,
      files: false,
      contacts: false,
    };
  });

  // Load saved state when site changes
  useEffect(() => {
    if (site?.id) {
      const saved = localStorage.getItem(`siteDrawerSections_${site.id}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setExpandedSections(parsed);
        } catch (e) {
          // If parsing fails, keep current state
        }
      }
    }
  }, [site?.id]);

  // Save expanded state to localStorage whenever it changes
  useEffect(() => {
    if (site?.id) {
      localStorage.setItem(`siteDrawerSections_${site.id}`, JSON.stringify(expandedSections));
    }
  }, [expandedSections, site?.id]);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev: typeof expandedSections) => ({ ...prev, [section]: !prev[section] }));
  };

  // Close submenu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only close if clicking outside the submenu container
      if (submenuRef.current && !submenuRef.current.contains(event.target as Node)) {
        // Use a small delay to allow click events to complete first
        setTimeout(() => {
          setShowAddDesignSubmenu(false);
          setShowDesignList(false);
        }, 0);
      }
    };

    if (showAddDesignSubmenu || showDesignList) {
      // Use click instead of mousedown to allow the button click to complete first
      document.addEventListener('click', handleClickOutside, true);
      return () => document.removeEventListener('click', handleClickOutside, true);
    }
  }, [showAddDesignSubmenu, showDesignList]);

  // Listen for job open event from sites list
  useEffect(() => {
    const handleOpenJob = (event: CustomEvent) => {
      const { jobId } = event.detail;
      if (!site || !currentSite) return;
      
      const job = currentSite.jobs?.find((j: Job) => j.id === jobId);
      if (job) {
        setSelectedJob(job);
        setDrawerView('job');
      }
    };

    window.addEventListener('openJobInDrawer', handleOpenJob as EventListener);
    return () => window.removeEventListener('openJobInDrawer', handleOpenJob as EventListener);
  }, [site, currentSite]);

  // Reset nested job view when the drawer closes
  useEffect(() => {
    if (!isOpen) {
      setDrawerView('site');
      setSelectedJob(null);
      setIsJobModalOpen(false);
    }
  }, [isOpen]);

  const activeJob = React.useMemo(() => {
    if (!selectedJob || !currentSite) return null;
    return currentSite.jobs?.find((j) => j.id === selectedJob.id) ?? selectedJob;
  }, [selectedJob, currentSite]);

  const activeRecurrenceRule = React.useMemo(() => {
    if (!activeJob?.recurrenceRuleId) return null;
    return recurrenceRules.find((r) => r.id === activeJob.recurrenceRuleId) ?? null;
  }, [activeJob, recurrenceRules]);

  const editingRecurrenceRule = editingRecurrenceSchedule && activeRecurrenceRule
    ? activeRecurrenceRule
    : null;

  const handleCreateNew = () => {
    setShowAddDesignSubmenu(false);
    if (onSwitchToDesigner) {
      onSwitchToDesigner();
    }
    if (onOpenDesignModal) {
      // Small delay to ensure tab switch happens first
      setTimeout(() => {
        onOpenDesignModal();
      }, 100);
    }
  };

  const handleAddExisting = () => {
    setShowAddDesignSubmenu(false);
    setShowDesignList(true);
  };

  const handleSelectDesign = (designId: string) => {
    if (!site || !currentSite) return;
    
    const currentDesigns = currentSite.networkDesigns || [];
    if (currentDesigns.includes(designId)) {
      setShowDesignList(false);
      return;
    }
    
    void saveSite({
      ...currentSite,
      networkDesigns: [...currentDesigns, designId],
      updatedAt: new Date(),
    });
    
    setShowDesignList(false);
  };

  const handleRemoveDesign = (designId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!site || !currentSite) return;
    
    const currentDesigns = currentSite.networkDesigns || [];
    const updatedDesigns = currentDesigns.filter((id: string) => id !== designId);
    
    void saveSite({
      ...currentSite,
      networkDesigns: updatedDesigns,
      updatedAt: new Date(),
    });
  };

  const handleOpenDesign = (designId: string) => {
    openDesignTab(designId);
    if (onSwitchToDesigner) {
      onSwitchToDesigner();
    }
  };

  const siteInProject = currentSite;
  const availableDesigns = getDesignsForCurrentProject();
  const siteDesignIds = siteInProject?.networkDesigns || [];
  const siteDesigns = availableDesigns.filter((d: { id: string }) => siteDesignIds.includes(d.id));
  const unassignedDesigns = availableDesigns.filter((d: { id: string }) => !siteDesignIds.includes(d.id));

  // Get jobs for the current site
  const siteJobs = siteInProject?.jobs || currentSite?.jobs || [];

  const generateId = () => generateSiteId();

  const handleAddJob = () => {
    setSelectedJob(null);
    setEditingRecurrenceSchedule(false);
    setIsJobModalOpen(true);
  };

  const handleJobClick = (job: Job) => {
    setSelectedJob(job);
    setDrawerView('job');
  };

  const handleSaveJobDirect = (updatedJob: Job) => {
    if (!currentSite) return;
    const currentJobs = currentSite.jobs || [];
    const updatedJobs = currentJobs.map((j) => (j.id === updatedJob.id ? updatedJob : j));
    void saveSiteJobs(currentSite.id, updatedJobs);
    setSelectedJob(updatedJob);
  };

  const handleSaveJob = (payload: JobSavePayload, siteId: string | null) => {
    const targetSiteId = siteId || site?.id || null;

    if (payload.recurrence) {
      if (!targetSiteId) return;
      if (editingRecurrenceSchedule && selectedJob?.recurrenceRuleId) {
        void updateRecurrenceRule(selectedJob.recurrenceRuleId, payload.job, payload.recurrence).then((ok) => {
          if (ok) {
            setIsJobModalOpen(false);
            setEditingRecurrenceSchedule(false);
          }
        });
        return;
      }

      void createRecurrenceRule(targetSiteId, payload.job, payload.recurrence).then((ok) => {
        if (ok) setIsJobModalOpen(false);
      });
      return;
    }

    const jobData = payload.job;

    if (selectedJob) {
      if (selectedJob.recurrenceRuleId && recurrenceRules.some((r) => r.id === selectedJob.recurrenceRuleId)) {
        return;
      }

      if (!targetSiteId) return;
      const targetSite = sites.find((s) => s.id === targetSiteId);
      if (!targetSite) return;

      const currentJobs = targetSite.jobs || [];
      const oldSite = sites.find((s) => s.jobs?.some((j: Job) => j.id === selectedJob.id));

      if (oldSite && oldSite.id !== targetSiteId) {
        const oldSiteJobs = (oldSite.jobs || []).filter((job: Job) => job.id !== selectedJob.id);
        const updatedJobs = [...currentJobs, { ...selectedJob, ...jobData, updatedAt: new Date() }];
        void saveSiteJobs(oldSite.id, oldSiteJobs);
        void saveSiteJobs(targetSiteId, updatedJobs);
        setIsJobModalOpen(false);
        return;
      }

      const updatedJobs = currentJobs.map((job: Job) =>
        job.id === selectedJob.id
          ? { ...job, ...jobData, updatedAt: new Date() }
          : job
      );
      void saveSiteJobs(targetSiteId, updatedJobs);
      setIsJobModalOpen(false);
      return;
    }

    const newJob: Job = {
      id: generateId(),
      ...jobData,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    void createJob(newJob, targetSiteId);
    setIsJobModalOpen(false);
  };

  return (
    <>
      {/* Drawer - always in DOM, just transformed off-screen */}
      <div
        className={`
          fixed top-0 right-0 h-full w-96 text-white border-l z-[80] ${isDarkTheme ? 'site-details-dark-neutral bg-[#171a1f] border-[#343a43]' : 'bg-gray-800 border-gray-700'}
          transition-transform duration-300 ease-out
          ${shouldShow ? 'translate-x-0' : 'translate-x-full'}
          flex flex-col shadow-2xl
          pointer-events-auto
        `}
        style={{
          willChange: 'transform',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sliding panels: site details ↔ job detail */}
        <div className="flex-1 overflow-hidden">
          <div
            className="flex h-full transition-transform duration-300 ease-out"
            style={{
              width: '200%',
              transform: drawerView === 'job' ? 'translateX(-50%)' : 'translateX(0)',
            }}
          >
            {/* Site panel */}
            <div className="w-1/2 h-full flex flex-col overflow-hidden">
              {/* Header */}
              <div className={`flex-shrink-0 px-4 py-2 border-b ${isDarkTheme ? 'border-[#343a43]' : 'border-gray-700'}`}>
                <div className="flex items-center justify-between">
                  <h2 className={`text-lg font-semibold truncate pr-2 ${isDarkTheme ? 'text-gray-200' : 'text-blue-400'}`}>
                    {currentSite ? currentSite.name : 'Site Details'}
                  </h2>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-700 rounded-md transition-colors flex-shrink-0"
                  >
                    <X size={18} className="text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Site content */}
              <div className="flex-1 overflow-y-auto custom-scrollbar px-2 pt-2 pb-4">
          {currentSite ? (
            <div className="space-y-0">
              {/* Site Details Section */}
              <div className="relative z-0">
                <button
                  onClick={() => toggleSection('siteDetails')}
                  className="w-full flex items-center justify-between py-2 pl-2 pr-3 hover:bg-gray-700/50 transition-colors rounded relative z-10"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`transition-transform duration-200 ${expandedSections.siteDetails ? 'rotate-0' : '-rotate-90'}`}>
                      <ChevronDown size={14} className="text-gray-400" />
                    </div>
                    <span className="text-sm font-medium text-white">Site Details</span>
                  </div>
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    expandedSections.siteDetails ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-3 pb-3 pt-1 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                        Site Name
                      </label>
                      <p className="text-sm text-white">{currentSite.name}</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                        Location
                      </label>
                      <p className="text-sm text-white">{currentSite.location}</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                        Tower Height
                      </label>
                      <p className="text-sm text-white">{currentSite.tower.height}m</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                        Tower Shape
                      </label>
                      <p className="text-sm text-white capitalize">{currentSite.tower.shape}</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
                        Equipment Count
                      </label>
                      <p className="text-sm text-white">{currentSite.equipment.length}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Collapsible Sections */}
                {/* Network Section */}
              <div className="relative z-0">
                <button
                  onClick={() => toggleSection('network')}
                  className="w-full flex items-center justify-between py-2 pl-2 pr-3 hover:bg-gray-700/50 transition-colors rounded relative z-10"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`transition-transform duration-200 ${expandedSections.network ? 'rotate-0' : '-rotate-90'}`}>
                      <ChevronDown size={14} className="text-gray-400" />
                    </div>
                    <span className="text-sm font-medium text-white">Network</span>
                  </div>
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    expandedSections.network ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-3 pb-3 pt-1 space-y-2">
                    {/* Add Design Button */}
                    <div className="relative" ref={submenuRef}>
                      <button
                        onClick={() => setShowAddDesignSubmenu(!showAddDesignSubmenu)}
                        className="w-1/3 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-transparent border border-gray-500 text-white hover:bg-white/10 rounded-md transition-colors text-xs font-medium"
                      >
                        <Plus size={12} />
                        Add Design
                      </button>
                      
                      {/* Submenu */}
                      {showAddDesignSubmenu && (
                        <div className="absolute top-full left-0 mt-1 w-full bg-gray-700 border border-gray-600 rounded-md shadow-lg z-10 overflow-hidden">
                          <button
                            onClick={handleCreateNew}
                            className="w-full text-left px-3 py-2 hover:bg-gray-600 text-sm text-white transition-colors"
                          >
                            Create new
                          </button>
                          <button
                            onClick={handleAddExisting}
                            className="w-full text-left px-3 py-2 hover:bg-gray-600 text-sm text-white transition-colors border-t border-gray-600"
                          >
                            Add existing
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Design List (when adding existing) */}
                    {showDesignList && (
                      <div 
                        className="bg-gray-700 border border-gray-600 rounded-md p-2 max-h-48 overflow-y-auto custom-scrollbar"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {unassignedDesigns.length === 0 ? (
                          <p className="text-xs text-gray-400 text-center py-2">No available designs</p>
                        ) : (
                          <div className="space-y-1">
                            {unassignedDesigns.map(design => (
                              <button
                                key={design.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectDesign(design.id);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                className="w-full text-left px-2 py-1.5 hover:bg-gray-600 rounded text-xs text-white transition-colors"
                              >
                                {design.name}
                              </button>
                            ))}
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowDesignList(false);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="w-full mt-2 px-2 py-1 text-xs text-gray-400 hover:text-white transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    )}

                    {/* Design Badges */}
                    {siteDesigns.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {siteDesigns.map(design => (
                          <div
                            key={design.id}
                            onClick={() => handleOpenDesign(design.id)}
                            className="group flex items-center gap-1.5 px-2 py-1 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-md cursor-pointer transition-colors"
                          >
                            <span className="text-xs text-white">{design.name}</span>
                            <button
                              onClick={(e) => handleRemoveDesign(design.id, e)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-gray-500 rounded"
                            >
                              <X size={10} className="text-gray-300" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Debug info - shows count of designs */}
                    {siteDesignIds.length > 0 && siteDesigns.length === 0 && (
                      <div className="mt-2 text-xs text-yellow-400">
                        Warning: {siteDesignIds.length} design(s) assigned but not found in project
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Jobs Section */}
              <div className="relative z-0">
                <button
                  onClick={() => toggleSection('jobs')}
                  className="w-full flex items-center justify-between py-2 pl-2 pr-3 hover:bg-gray-700/50 transition-colors rounded relative z-10"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`transition-transform duration-200 ${expandedSections.jobs ? 'rotate-0' : '-rotate-90'}`}>
                      <ChevronDown size={14} className="text-gray-400" />
                    </div>
                    <span className="text-sm font-medium text-white">Jobs</span>
                  </div>
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    expandedSections.jobs ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-3 pb-3 pt-1 space-y-2">
                    {/* Add Job Button */}
                    <div className="relative">
                      <button
                        onClick={handleAddJob}
                        className="w-1/3 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-transparent border border-gray-500 text-white hover:bg-white/10 rounded-md transition-colors text-xs font-medium"
                      >
                        <Plus size={12} />
                        Add Job
                      </button>
                    </div>

                    {/* Jobs List */}
                    {siteJobs.length > 0 && (
                      <div className="space-y-1.5 mt-2">
                        {siteJobs.map((job: Job) => (
                          <div
                            key={job.id}
                            onClick={() => handleJobClick(job)}
                            className="p-2 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-md cursor-pointer transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium text-white truncate">{job.title}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                    job.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                    job.status === 'in-progress' ? 'bg-blue-500/20 text-blue-400' :
                                    job.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                    'bg-gray-500/20 text-gray-400'
                                  }`}>
                                    {job.status}
                                  </span>
                                  {job.priority && (
                                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                      job.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                                      job.priority === 'medium' ? 'bg-orange-500/20 text-orange-400' :
                                      'bg-gray-500/20 text-gray-400'
                                    }`}>
                                      {job.priority}
                                    </span>
                                  )}
                                </div>
                                {job.description && (
                                  <p className="text-xs text-gray-400 truncate">{job.description}</p>
                                )}
                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                  {job.assignedTo && (
                                    <span>Assigned: {job.assignedTo}</span>
                                  )}
                                  {job.dueDate && (
                                    <span>Due: {new Date(job.dueDate).toLocaleDateString()}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {siteJobs.length === 0 && (
                      <p className="text-xs text-gray-400 mt-2">No jobs yet. Click "Add Job" to create one.</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Equipment Section */}
              <div className="relative z-0">
                <button
                  onClick={() => toggleSection('equipment')}
                  className="w-full flex items-center justify-between py-2 pl-2 pr-3 hover:bg-gray-700/50 transition-colors rounded relative z-10"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`transition-transform duration-200 ${expandedSections.equipment ? 'rotate-0' : '-rotate-90'}`}>
                      <ChevronDown size={14} className="text-gray-400" />
                    </div>
                    <span className="text-sm font-medium text-white">Equipment</span>
                  </div>
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    expandedSections.equipment ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-3 pb-3 pt-1 space-y-2">
                    {(() => {
                      // Canvas equipment from associated design(s)
                      const currentDesign = getCurrentDesign();
                      let equipmentList: any[] = [];

                      if (currentDesign) {
                        const isAssociated = !currentSite.networkDesigns ||
                                          currentSite.networkDesigns.length === 0 ||
                                          currentSite.networkDesigns.includes(currentDesign.id);

                        if (isAssociated) {
                          equipmentList = currentDesign.equipment || [];
                        }
                      }

                      // Racks linked to this site (across all designs in the collection)
                      const linkedRacks: { rack: Rack; designId: string; designName: string }[] = [];
                      for (const design of getDesignsForCurrentProject()) {
                        for (const rack of design.racks || []) {
                          if (rack.siteId === currentSite.id) {
                            linkedRacks.push({
                              rack,
                              designId: design.id,
                              designName: design.name || 'Design',
                            });
                          }
                        }
                      }

                      const totalCount = equipmentList.length + linkedRacks.length;

                      const openLinkedRack = (rack: Rack, designId: string) => {
                        openDesignTab(designId);
                        setSelectedRack(rack);
                        setRackBuilderOpen(true);
                        onSwitchToDesigner?.();
                      };

                      return (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                              Equipment Count: {totalCount}
                            </span>
                          </div>

                          {linkedRacks.length > 0 && (
                            <div className="space-y-1.5 mt-2">
                              <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                                Racks
                              </span>
                              {linkedRacks.map(({ rack, designId, designName }) => (
                                <button
                                  key={rack.id}
                                  type="button"
                                  onClick={() => openLinkedRack(rack, designId)}
                                  className="w-full text-left p-2 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600/80 hover:border-gray-500 transition-colors"
                                  title="Open rack builder"
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div className="flex-shrink-0 w-6 h-6 rounded bg-slate-800 border border-gray-600 flex items-center justify-center">
                                      <Server size={12} className="text-gray-200" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-0.5">
                                        <span className="text-xs font-medium text-white truncate">
                                          {rack.name}
                                        </span>
                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-300 flex-shrink-0">
                                          {rack.size}U
                                        </span>
                                        {rack.isExisting && (
                                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-400 flex-shrink-0">
                                            Existing
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-400 truncate">
                                        Rack · {rack.equipment?.length || 0} items · {designName}
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}

                          {equipmentList.length > 0 ? (
                            <div className="space-y-1.5 mt-2">
                              {linkedRacks.length > 0 && (
                                <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                                  Devices
                                </span>
                              )}
                              {equipmentList.map((equipment) => (
                                <div
                                  key={equipment.id}
                                  className="p-2 bg-gray-700 border border-gray-600 rounded-md"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs font-medium text-white truncate">
                                          {equipment.config?.label || equipment.equipment.name}
                                        </span>
                                        {equipment.isExisting && (
                                          <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400">
                                            Existing
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-400 capitalize">
                                        {equipment.equipment.type.replace('-', ' ')}
                                      </div>
                                      {equipment.config?.ip && (
                                        <div className="text-xs text-gray-500 mt-1">
                                          IP: {equipment.config.ip}
                                        </div>
                                      )}
                                      {equipment.config?.ssid && (
                                        <div className="text-xs text-gray-500 mt-1">
                                          SSID: {equipment.config.ssid}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : linkedRacks.length === 0 ? (
                            <p className="text-xs text-gray-400 mt-2">
                              No equipment or linked racks for this site.
                            </p>
                          ) : null}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Maintenance Section */}
              <div className="relative z-0">
                <button
                  onClick={() => toggleSection('maintenance')}
                  className="w-full flex items-center justify-between py-2 pl-2 pr-3 hover:bg-gray-700/50 transition-colors rounded relative z-10"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`transition-transform duration-200 ${expandedSections.maintenance ? 'rotate-0' : '-rotate-90'}`}>
                      <ChevronDown size={14} className="text-gray-400" />
                    </div>
                    <span className="text-sm font-medium text-white">Maintenance</span>
                  </div>
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    expandedSections.maintenance ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-3 pb-3 pt-1">
                    <p className="text-xs text-gray-400">Maintenance records will be available here.</p>
                  </div>
                </div>
              </div>

              {/* Storage Section */}
              <div className="relative z-0">
                <button
                  onClick={() => toggleSection('storage')}
                  className="w-full flex items-center justify-between py-2 pl-2 pr-3 hover:bg-gray-700/50 transition-colors rounded relative z-10"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`transition-transform duration-200 ${expandedSections.storage ? 'rotate-0' : '-rotate-90'}`}>
                      <ChevronDown size={14} className="text-gray-400" />
                    </div>
                    <span className="text-sm font-medium text-white">Storage</span>
                  </div>
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    expandedSections.storage ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-3 pb-3 pt-1">
                    <p className="text-xs text-gray-400">Storage information will be available here.</p>
                  </div>
                </div>
              </div>

              {/* Files Section */}
              <div className="relative z-0">
                <button
                  onClick={() => toggleSection('files')}
                  className="w-full flex items-center justify-between py-2 pl-2 pr-3 hover:bg-gray-700/50 transition-colors rounded relative z-10"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`transition-transform duration-200 ${expandedSections.files ? 'rotate-0' : '-rotate-90'}`}>
                      <ChevronDown size={14} className="text-gray-400" />
                    </div>
                    <span className="text-sm font-medium text-white">Files</span>
                  </div>
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    expandedSections.files ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-3 pb-3 pt-1">
                    <p className="text-xs text-gray-400">File management will be available here.</p>
                  </div>
                </div>
              </div>

              {/* Contacts Section */}
              <div className="relative z-0">
                <button
                  onClick={() => toggleSection('contacts')}
                  className="w-full flex items-center justify-between py-2 pl-2 pr-3 hover:bg-gray-700/50 transition-colors rounded relative z-10"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`transition-transform duration-200 ${expandedSections.contacts ? 'rotate-0' : '-rotate-90'}`}>
                      <ChevronDown size={14} className="text-gray-400" />
                    </div>
                    <span className="text-sm font-medium text-white">Contacts</span>
                  </div>
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    expandedSections.contacts ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-3 pb-3 pt-1">
                    <p className="text-xs text-gray-400">Contact information will be available here.</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-400 py-8">
              <p className="text-sm">No site selected</p>
            </div>
          )}
              </div>
            </div>

            {/* Job panel */}
            <div className="w-1/2 h-full flex flex-col overflow-hidden">
              {activeJob && currentSite && drawerView === 'job' && (
                <JobDrawer
                  embedded
                  job={activeJob}
                  recurrenceRule={activeRecurrenceRule}
                  site={currentSite}
                  onBack={() => setDrawerView('site')}
                  onClose={onClose}
                  onEdit={() => {
                    setEditingRecurrenceSchedule(false);
                    setIsJobModalOpen(true);
                  }}
                  onEditSchedule={activeRecurrenceRule ? () => {
                    setEditingRecurrenceSchedule(true);
                    setIsJobModalOpen(true);
                  } : undefined}
                  onSave={handleSaveJobDirect}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Job Modal */}
      <JobModal
        isOpen={isJobModalOpen}
        onClose={() => {
          setIsJobModalOpen(false);
          setEditingRecurrenceSchedule(false);
        }}
        job={editingRecurrenceSchedule ? null : selectedJob}
        recurrenceRule={editingRecurrenceRule}
        onSave={handleSaveJob}
        onEndRecurrence={editingRecurrenceSchedule && activeJob?.recurrenceRuleId
          ? async (lastOccurrenceDate) => {
            const ok = await endRecurrenceRule(activeJob.recurrenceRuleId!, lastOccurrenceDate);
            if (ok) {
              setIsJobModalOpen(false);
              setEditingRecurrenceSchedule(false);
            }
            return ok;
          }
          : undefined}
        sites={sites}
        selectedSiteId={currentSite?.id || null}
      />
    </>
  );
};

export default SiteDetailsDrawer;

